/**
 * Uniform-grid spatial hash for point-like agents, rebuilt every few steps.
 * Buckets are reused between rebuilds so a rebuild allocates nothing in steady state.
 */
export class SpatialHash<T> {
  private readonly cells = new Map<number, T[]>();
  private readonly spare: T[][] = [];
  private readonly inv: number;

  constructor(readonly cellSize: number) {
    this.inv = 1 / cellSize;
  }

  private key(ix: number, iz: number): number {
    return (ix + 32768) * 65536 + (iz + 32768);
  }

  clear(): void {
    for (const bucket of this.cells.values()) {
      bucket.length = 0;
      this.spare.push(bucket);
    }
    this.cells.clear();
  }

  insert(x: number, z: number, item: T): void {
    const k = this.key(Math.floor(x * this.inv), Math.floor(z * this.inv));
    let bucket = this.cells.get(k);
    if (!bucket) {
      bucket = this.spare.pop() ?? [];
      this.cells.set(k, bucket);
    }
    bucket.push(item);
  }

  /** Calls `fn` for every item in cells overlapping the square [x±r, z±r] (a superset of the circle). */
  forEachNear(x: number, z: number, r: number, fn: (item: T) => void): void {
    const x0 = Math.floor((x - r) * this.inv);
    const x1 = Math.floor((x + r) * this.inv);
    const z0 = Math.floor((z - r) * this.inv);
    const z1 = Math.floor((z + r) * this.inv);
    for (let ix = x0; ix <= x1; ix++) {
      for (let iz = z0; iz <= z1; iz++) {
        const bucket = this.cells.get(this.key(ix, iz));
        if (!bucket) continue;
        for (let i = 0; i < bucket.length; i++) fn(bucket[i] as T);
      }
    }
  }
}
